import React, { useCallback, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

/**
 * Bridges the Knowledge Curator (content-area screen) to the space page tree in the sidebar.
 * `<Subscribe>` resolves the same lazily-created `PageTreeStateUpdater` instance the page tree
 * subscribes to (single root unstated Provider), so calling `setForceFetchPageId` /
 * `setOptimisticDeleteId` here drives a `network-only` refetch or optimistic-remove of the tree.
 *
 * NOTE: This is a deliberate copy of the twin in `@confluence/space-curation`
 * (`src/PageTreeForceFetchSubscriber.tsx`). Both are team Disco; the shared home
 * (`@confluence/page-tree-refresh-state-container`) is owned by another team and flagged
 * high-risk, so a 40-line copy is preferred over a cross-team shared API for two consumers. Keep
 * the name and `forceFetchPageIds` contract identical so the two can be reconciled into a shared
 * entry point if a third consumer ever appears.
 */

export type ForceFetchPageIds = (pageIds: Array<number | string>) => void;

/**
 * Optimistically drops one or more page ids from the tree via
 * `PageTreeStateUpdater.setOptimisticDeleteId`. Use this — not `forceFetchPageIds` — for DELETE:
 * refetching a deleted content id (or its parent) does not remove the node from the tree's
 * children list, but this hook does.
 */
export type OptimisticDeletePageIds = (pageIds: Array<number | string>) => void;

type PageTreeForceFetchSubscriberProps = {
	onForceFetchPageIdsChange: (forceFetchPageIds: ForceFetchPageIds | null) => void;
	onOptimisticDeletePageIdsChange?: (
		optimisticDeletePageIds: OptimisticDeletePageIds | null,
	) => void;
};

type PageTreeForceFetchRegistrationProps = PageTreeForceFetchSubscriberProps & {
	pageTreeStateUpdater: PageTreeStateUpdater;
};

const PageTreeForceFetchRegistration = ({
	pageTreeStateUpdater,
	onForceFetchPageIdsChange,
	onOptimisticDeletePageIdsChange,
}: PageTreeForceFetchRegistrationProps) => {
	const forceFetchPageIds = useCallback<ForceFetchPageIds>(
		(pageIds) => {
			const uniquePageIds = new Set<string>();
			pageIds.forEach((pageId) => {
				const normalizedPageId = String(pageId);
				if (normalizedPageId) {
					uniquePageIds.add(normalizedPageId);
				}
			});
			uniquePageIds.forEach((pageId) => {
				pageTreeStateUpdater.setForceFetchPageId(pageId);
			});
		},
		[pageTreeStateUpdater],
	);

	const optimisticDeletePageIds = useCallback<OptimisticDeletePageIds>(
		(pageIds) => {
			const uniquePageIds = new Set<string>();
			pageIds.forEach((pageId) => {
				const normalizedPageId = String(pageId);
				if (normalizedPageId) {
					uniquePageIds.add(normalizedPageId);
				}
			});
			// `optimisticDeletePageId` is a single-slot state field consumed synchronously
			// by `PageTreeCoordinator` (see `optimisticDeleteContent` + `clearDeleteId`).
			// Calling it once per id is fine in the common single-delete case; back-to-back
			// deletes rely on the coordinator clearing the slot between updates.
			uniquePageIds.forEach((pageId) => {
				pageTreeStateUpdater.setOptimisticDeleteId(pageId);
			});
		},
		[pageTreeStateUpdater],
	);

	useEffect(() => {
		onForceFetchPageIdsChange(forceFetchPageIds);

		return () => onForceFetchPageIdsChange(null);
	}, [forceFetchPageIds, onForceFetchPageIdsChange]);

	useEffect(() => {
		onOptimisticDeletePageIdsChange?.(optimisticDeletePageIds);

		return () => onOptimisticDeletePageIdsChange?.(null);
	}, [optimisticDeletePageIds, onOptimisticDeletePageIdsChange]);

	return null;
};

export const PageTreeForceFetchSubscriber = ({
	onForceFetchPageIdsChange,
	onOptimisticDeletePageIdsChange,
}: PageTreeForceFetchSubscriberProps): React.JSX.Element => (
	<Subscribe to={[PageTreeStateUpdater]}>
		{(pageTreeStateUpdater: PageTreeStateUpdater) => (
			<PageTreeForceFetchRegistration
				pageTreeStateUpdater={pageTreeStateUpdater}
				onForceFetchPageIdsChange={onForceFetchPageIdsChange}
				onOptimisticDeletePageIdsChange={onOptimisticDeletePageIdsChange}
			/>
		)}
	</Subscribe>
);
